import { ExtendedWarrantyLineConfig } from '@Calix-Commerce/extended-warranty-component';
import { Currency, CurrentQuoteState } from 'types';
import { LineItem, QuoteGroup as QuoteGroupNameAndId } from 'types';

export type Quote = {
  projectId: string;
  quoteName: string;
  quoteNumber: string;
  quoteDescription: string | null;
  archived: boolean;
  option: string;
  revisionNumber: string;
  tags: string[];
  preparedByName: string;
  accountId: string;
  calculatedOneTimeDiscount: number;
  containsNonDiscoutedDiscountableItems: boolean;
  createdDate: string;
  currencyCode: Currency;
  eCommercePermissions: string[];
  isOrdered: boolean;
  lastModifiedDate: string;
  lockExpiry: string;
  lockedBy: string;
  opportunityId: string;
  poNumber: string;
  quoteStatus: string; // Approved |
  total: number;
  transactionId: string;
  warrantyTotal: number;
  warrantyInput: ExtendedWarrantyLineConfig[];
};

export type AddressInput = {
  name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
  province?: string | null;
};

export type LineItemDetailInput = {
  action: string;
  documentNumber: number;
  partNumber: string;
  // Information below is not expected during item deletion
  groupNumber?: number;
  quantity?: number;
  sequenceNumber?: number;
};

export type QuoteGroupInput = {
  name: string | null;
};

export type ShipmentScheduleInput = {
  lines: ShipmentScheduleLineInput[];
  shipDate: string;
};

export type ShipmentScheduleLineInput = {
  partNumber: string;
  quantity: number;
};

export type PhoneInput = {
  areaCode: number | null;
  countryCode: number | null;
  extension: string | null;
  number: string | null;
};

export type QuoteInformation = CurrentQuoteState & {
  submitOrder?: boolean;
  unlock?: boolean;
};

export type QuoteInput = {
  accountId?: string;
  archived?: boolean;
  billingAddress?: AddressInput;
  confirmationRecipients?: string;
  currencyCode?: string;
  lesingAgent?: boolean;
  lineItems?: LineItemDetailInput[];
  lockedBy?: string;
  lockExpiry?: string;
  multipleShipments?: string;
  option?: string;
  packingInstructions?: string;
  poNumber?: string;
  csapEnrolled?: boolean;
  priceBook?: string;
  quoteDescription?: string | null;
  quoteGroups?: QuoteGroupInput[];
  quoteName?: string;
  requestedShippingDate?: string;
  requestedShippingType?: string;
  revisionNumber?: number;
  shipmentSchedule?: ShipmentScheduleInput[];
  shippingAddress?: AddressInput;
  shippingContactEmail?: string;
  shippingContactFirstName?: string;
  shippingContactLastName?: string;
  shippingContactPhone?: PhoneInput;
  shippingInstructions?: string;
  submitOrder?: boolean;
  tags?: string[];
  taxExempt?: string;
  unlock?: boolean;
};

export type QuoteGroupItem = LineItem & {
  id: string;
  imgSource: string;
  itemNotes?: string[];
  priceWarnings?: string[];
  itemWarnings?: string[];
};

export type QuoteGroupDetail = {
  id: number;
  items: QuoteGroupItem[];
  groupName: string;
  isUnassigned: boolean;
  displaySubTotal: string;
  subTotalText?: string;
};

export type QuoteDetailTableProps = {
  quoteGroups: QuoteGroupDetail[];
  handleQuoteUpdate?: (newQuoteGroups: QuoteGroupDetail[]) => void;
  handleRenameGroup?: (quoteGroupId: number, newName: string) => void;
  handleRemoveItem?: (removedItem: LineItem) => void;
  handleReorderGroups?: (groupOrder: QuoteGroupNameAndId[]) => void;
  onRemoveGroup?: (removedGroup: QuoteGroupDetail) => void;
  onCloneGroup?: (clonedGroup: QuoteGroupDetail) => void;
  handleAddPart?: (partNumber: string, groupId: number, groupName: string) => void;
  searchPartNumbers?: (searchTerm: string, groupId: number) => void;
  searchResults?: { id: number; results: { partNumber: string }[] }[];
  readonly: boolean;
  quoteTotalPrice: string;
  oneTimeDiscountPrice?: string | null;
  allowRemovalOnReadOnly?: boolean;
  isQuoteOrdered?: boolean;
};

export type QuoteTableRowActions = {
  handleItemUpdate?: (groupId: number, newItem: QuoteGroupItem) => void;
  handleOnItemDrag?: (e: React.DragEvent, itemId: string, groupId: string) => void;
  handleRemoveItem?: (removedItem: LineItem) => void;
};

export type QuoteTableRowsProps = {
  item: QuoteGroupItem;
  groupId: number;
  index: number;
  readonly?: boolean;
  isQuoteOrdered?: boolean;
  allowRemovalOnReadOnly?: boolean;
};

export enum CreatedBySystem {
  ECOM = 'ECOM',
  EquipmentOrderDocument = 'EquipmentOrderDocument',
}
